import { useMemo, useRef, useEffect } from "react";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Task } from "@/types/dashboard";

interface TaskListProps {
  tasks: Record<string, Task>;
  onTaskClick?: (questionId: string) => void;
  totalQuestions?: number;
}

export function TaskList({ tasks, onTaskClick }: TaskListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const sortedTasks = useMemo(() => {
    const taskArray = tasks ? Object.values(tasks) : [];

    // Simply return sorted tasks without generating placeholders
    // The backend should send all tasks (including queued ones)
    return taskArray.sort((a, b) => {
      // Extract question number and run number for proper sorting
      // Format: q001_finance_run1, q002_hr_run2, etc.
      const parseId = (id: string) => {
        const match = id.match(/q(\d+).*_run(\d+)/);
        if (match) {
          return {
            question: parseInt(match[1], 10),
            run: parseInt(match[2], 10)
          };
        }
        return { question: 0, run: 0 };
      };

      const parsedA = parseId(a.question_id);
      const parsedB = parseId(b.question_id);

      // Sort by question number first, then by run number
      if (parsedA.question !== parsedB.question) {
        return parsedA.question - parsedB.question;
      }
      return parsedA.run - parsedB.run;
    });
  }, [tasks]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [sortedTasks.length]);

  if (sortedTasks.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Clock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No tasks yet...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={listRef} className="h-full space-y-2 overflow-y-auto pr-2">
      {sortedTasks.map((task) => (
        <TaskItem key={task.question_id} task={task} onClick={onTaskClick} />
      ))}
    </div>
  );
}

function TaskItem({ task, onClick }: { task: Task; onClick?: (questionId: string) => void }) {
  const isPlaceholder = task.question === 'Waiting to start...';

  const statusConfig = useMemo(() => {
    // Check if this is a placeholder (not yet started)
    if (isPlaceholder) {
      return {
        icon: Clock,
        bgColor: "bg-muted/10 border-muted/30",
        textColor: "text-muted-foreground",
        iconColor: "text-muted-foreground",
        label: "NOT STARTED",
      };
    }

    switch (task.status) {
      case "queued":
        return {
          icon: Clock,
          bgColor: "bg-blue-500/10 border-blue-500/30 hover:border-blue-500/50",
          textColor: "text-blue-400",
          iconColor: "text-blue-500",
          label: "QUEUED",
        };
      case "running":
        return {
          icon: Loader2,
          bgColor: "bg-warning/10 border-warning/30 hover:border-warning/50 glow",
          textColor: "text-warning",
          iconColor: "text-warning",
          label: "RUNNING",
        };
      case "completed":
      case "success":
        return {
          icon: CheckCircle2,
          bgColor: "bg-success/10 border-success/30 hover:border-success/50",
          textColor: "text-success",
          iconColor: "text-success",
          label: "COMPLETED",
        };
      case "failed":
      case "error":
        return {
          icon: XCircle,
          bgColor: "bg-destructive/10 border-destructive/30 hover:border-destructive/50",
          textColor: "text-destructive",
          iconColor: "text-destructive",
          label: "FAILED",
        };
      default:
        return {
          icon: Clock,
          bgColor: "bg-muted/10 border-muted/30",
          textColor: "text-muted-foreground",
          iconColor: "text-muted-foreground",
          label: (task.status as string)?.toUpperCase() || "UNKNOWN",
        };
    }
  }, [task.status, isPlaceholder]);

  const Icon = statusConfig.icon;

  const getScoreColor = (score: number) => {
    if (score >= 4.5) return "from-success to-green-400";
    if (score >= 3.5) return "from-warning to-yellow-400";
    if (score >= 2.5) return "from-orange-500 to-orange-400";
    return "from-destructive to-red-400";
  };

  const isClickable = !isPlaceholder && (task.status === "running" || task.status === "completed" || task.status === "success" || task.status === "failed" || task.status === "error") && onClick;

  return (
    <div
      className={cn(
        "group flex items-start gap-3 rounded-lg border p-3 transition-all duration-200 backdrop-blur-sm",
        statusConfig.bgColor,
        isClickable && "cursor-pointer hover:shadow-lg",
        isClickable && task.status === "running" && "hover:border-warning",
        isClickable && task.status === "completed" && "hover:border-success",
        isClickable && task.status === "failed" && "hover:border-destructive"
      )}
      onClick={() => isClickable && onClick(task.question_id)}
    >
      <div className={cn("mt-0.5 flex-shrink-0", statusConfig.iconColor)}>
        <Icon
          className={cn("h-4 w-4", task.status === "running" && "animate-spin")}
        />
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-bold uppercase tracking-wider", statusConfig.textColor)}>
              {statusConfig.label}
            </span>
            <span className="text-xs font-mono text-muted-foreground">
              {task.question_id}
            </span>
            {task.status === "running" && task.server_idx !== undefined && (
              <span className="rounded-md bg-warning/20 px-2 py-0.5 text-xs font-bold text-warning border border-warning/30">
                Server {task.server_idx}
              </span>
            )}
          </div>

          {(task.status === "completed" || task.status === "success") && task.accuracy_score !== undefined && (
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold bg-gradient-to-r",
                getScoreColor(task.accuracy_score),
                "text-white shadow-lg"
              )}
            >
              <span>{task.accuracy_score?.toFixed(1) ?? 'N/A'}</span>
              <span className="text-xs">/5</span>
              {task.sql_valid && <span className="text-xs">✓</span>}
            </div>
          )}
        </div>

        <p className="text-sm text-foreground/90 line-clamp-2 leading-relaxed">
          {task.question}
        </p>

        {task.status === "failed" && task.error && (
          <p className="text-xs text-destructive/90 line-clamp-1 font-medium" title={task.error}>
            ⚠ {task.error}
          </p>
        )}
      </div>
    </div>
  );
}
